const LINK_FIELDS = [{ name: "label", label: "Label" }, { name: "to", label: "Link (e.g. /marketplace or https://…)" }];
const PAGE_OPTIONS = ["/", "/marketplace", "/chefs", "/restaurants", "/discovery", "/partners", "/jollof", "/about", "/contact", "/track", "/delivery", "/refund", "/terms", "/privacy", "/sustainability", "/corporate", "/rewards", "/careers", "/checkout"].map((p) => ({ value: p, label: p }));

export const SCHEMAS = {
  general: {
    title: "General settings", description: "Your business details. These appear in the footer, emails and around the site.",
    groups: [
      { title: "Business", fields: [
        { name: "site_name", label: "Site name" }, { name: "tagline", label: "Slogan" },
        { name: "contact_phone", label: "Phone" }, { name: "contact_email", label: "Main email" },
        { name: "support_email", label: "Customer support email" }, { name: "business_address", label: "Business address" },
      ] },
      { title: "Regional", fields: [
        { name: "currency", label: "Currency", type: "select", options: [{ value: "EUR", label: "Euro (€)" }] },
        { name: "timezone", label: "Time zone", type: "select", options: ["Europe/Amsterdam", "Europe/Berlin", "Europe/Brussels", "Europe/London", "UTC"].map((v) => ({ value: v, label: v })) },
      ] },
    ],
  },
  notifications: {
    title: "Notification settings", description: "Choose which messages the platform sends. Email and text sending must also be switched on under Email & SMS.",
    groups: [
      { title: "Customers", fields: [
        { name: "order_confirmation_email", label: "Order confirmation email", type: "checkbox" },
        { name: "order_status_sms", label: "Order status text messages", type: "checkbox" },
        { name: "marketing_emails", label: "Marketing emails", type: "checkbox" },
      ] },
      { title: "Your team", fields: [
        { name: "new_order_admin_alert", label: "Alert on every new order", type: "checkbox" },
        { name: "chef_application_alert", label: "Alert on new chef applications", type: "checkbox" },
        { name: "weekly_report_email", label: "Weekly report email", type: "checkbox" },
        { name: "admin_alert_email", label: "Send team alerts to" },
      ] },
    ],
  },
  locations: {
    title: "Locations & service areas", description: "Where you deliver and what it costs. Fees here are used in the cart and at checkout.",
    groups: [
      { title: "Delivery pricing", fields: [
        { name: "default_delivery_fee", label: "Delivery fee (€)", type: "number" },
        { name: "free_delivery_over", label: "Free delivery over (€, 0 = never)", type: "number" },
        { name: "min_order", label: "Minimum order (€)", type: "number" },
      ] },
      { title: "Service areas", fields: [
        { name: "areas", label: "Areas", type: "list", itemLabel: "Area", fields: [
          { name: "city", label: "City" }, { name: "postcodes", label: "Postcodes" },
          { name: "delivery_fee", label: "Delivery fee (€)", type: "number" }, { name: "eta", label: "Delivery time" },
          { name: "active", label: "Active", type: "checkbox" },
        ] },
      ] },
    ],
  },
  email: {
    title: "Email integration", description: "Settings are saved, but no emails are sent yet. Sending will be switched on in a later step.",
    groups: [{ title: "Email provider", fields: [
      { name: "enabled", label: "Enable email sending", type: "checkbox" },
      { name: "provider", label: "Provider", type: "select", options: ["Resend", "SendGrid", "Mailgun", "SMTP"].map((v) => ({ value: v, label: v })) },
      { name: "from_name", label: "Sender name" }, { name: "from_email", label: "Sender email" },
      { name: "reply_to", label: "Reply-to email" }, { name: "api_key", label: "API key", type: "secret", help: "Stored securely and never shown in full." },
    ] }],
  },
  sms: {
    title: "SMS integration", description: "Settings are saved, but no texts are sent yet. Sending will be switched on in a later step.",
    groups: [{ title: "SMS provider", fields: [
      { name: "enabled", label: "Enable text messages", type: "checkbox" },
      { name: "provider", label: "Provider", type: "select", options: ["Twilio", "MessageBird", "Vonage"].map((v) => ({ value: v, label: v })) },
      { name: "sender_id", label: "Sender name / number" }, { name: "account_sid", label: "Account ID" },
      { name: "auth_token", label: "Auth token", type: "secret", help: "Stored securely and never shown in full." },
    ] }],
  },
  integrations: {
    title: "API & third-party integrations", description: "Keep track of connected services and their keys.",
    groups: [
      { title: "Analytics", fields: [{ name: "google_analytics_id", label: "Google Analytics ID" }, { name: "facebook_pixel_id", label: "Facebook Pixel ID" }] },
      { title: "Services", fields: [{ name: "services", label: "Connected services", type: "list", itemLabel: "Service", fields: [
        { name: "name", label: "Name" }, { name: "provider", label: "Provider" },
        { name: "api_key", label: "API key", type: "secret" }, { name: "enabled", label: "Enabled", type: "checkbox" },
        { name: "notes", label: "Notes", type: "textarea" },
      ] }] },
    ],
  },
  privacy: {
    title: "Privacy & security", description: "Sign-in rules, cookie notice and data retention.",
    groups: [
      { title: "Accounts & sign-in", fields: [
        { name: "allow_registration", label: "Allow new customer sign-ups", type: "checkbox" },
        { name: "min_password_length", label: "Minimum password length", type: "number" },
        { name: "max_login_attempts", label: "Failed sign-ins before lockout", type: "number" },
        { name: "lockout_minutes", label: "Lockout time (minutes)", type: "number" },
        { name: "session_timeout_minutes", label: "Session length (minutes)", type: "number" },
      ] },
      { title: "Privacy", fields: [
        { name: "cookie_banner", label: "Show cookie notice", type: "checkbox" },
        { name: "data_retention_days", label: "Keep order data for (days)", type: "number" },
        { name: "cookie_text", label: "Cookie notice text", type: "textarea" },
      ] },
    ],
  },
  languages: {
    title: "Languages", description: "Visitors can switch language in the header. With auto-detect on, the site opens in the visitor's browser language.",
    groups: [{ title: "Language options", fields: [
      { name: "enabled", label: "Languages offered", type: "multicheck", options: [{ value: "en", label: "English" }, { value: "nl", label: "Dutch" }, { value: "de", label: "German" }] },
      { name: "default", label: "Default language", type: "select", options: [{ value: "en", label: "English" }, { value: "nl", label: "Dutch" }, { value: "de", label: "German" }] },
      { name: "auto_detect", label: "Auto-detect visitor language", type: "checkbox" },
      { name: "show_switcher", label: "Show language switcher", type: "checkbox" },
    ] }],
  },
  homepage: {
    title: "Homepage", description: "Edit every section of your homepage. Turn sections on or off under Visibility.",
    groups: [
      { title: "Hero banner", fields: [
        { name: "hero_eyebrow", label: "Small heading" }, { name: "hero_tagline", label: "Tagline" },
        { name: "hero_title", label: "Title line 1" }, { name: "hero_title_2", label: "Title line 2" },
        { name: "hero_highlight", label: "Highlighted word (red, italic)" }, { name: "search_button", label: "Search button text" },
        { name: "search_placeholder", label: "Search box placeholder" }, { name: "stats", label: "Stats under search (comma separated)", type: "tags" },
        { name: "hero_subtitle", label: "Intro text", type: "textarea" }, { name: "hero_image", label: "Background image", type: "image" },
      ] },
      { title: "Featured content", help: "Leave empty to show everything.", fields: [
        { name: "featured_chef_ids", label: "Featured chefs & restaurants", type: "multicheck", options: "chefs" },
        { name: "featured_dish_ids", label: "Featured dishes", type: "multicheck", options: "dishes" },
      ] },
      { title: "Promotions strip", fields: [{ name: "promos", label: "Promotions", type: "list", itemLabel: "Promo", fields: [
        { name: "title", label: "Title" }, { name: "code", label: "Code" }, { name: "detail", label: "Detail", type: "textarea" },
      ] }] },
      { title: "Custom sections", help: "Add your own sections with text, an image and a button.", fields: [{ name: "sections", label: "Sections", type: "list", itemLabel: "Section", fields: [
        { name: "label", label: "Small heading" }, { name: "title", label: "Title" },
        { name: "button_text", label: "Button text" }, { name: "button_link", label: "Button link" },
        { name: "layout", label: "Image position", type: "select", options: [{ value: "image-left", label: "Image left" }, { value: "image-right", label: "Image right" }] },
        { name: "active", label: "Show", type: "checkbox" },
        { name: "text", label: "Text", type: "textarea" }, { name: "image", label: "Image", type: "image" },
      ] }] },
      { title: "Dish spotlight", fields: [
        { name: "spotlight_label", label: "Small heading" }, { name: "spotlight_title", label: "Title" },
        { name: "spotlight_button", label: "Button text" }, { name: "spotlight_link", label: "Button link" },
        { name: "spotlight_text", label: "Text", type: "textarea" }, { name: "spotlight_image", label: "Image", type: "image" },
      ] },
      { title: "Testimonials", fields: [{ name: "testimonials", label: "Testimonials", type: "list", itemLabel: "Testimonial", fields: [
        { name: "name", label: "Name" }, { name: "city", label: "City" }, { name: "rating", label: "Stars (1-5)", type: "number" },
        { name: "text", label: "Quote", type: "textarea" },
      ] }] },
      { title: "App & chef call-to-actions", fields: [
        { name: "app_title", label: "App title" }, { name: "app_store_url", label: "App Store link" },
        { name: "play_store_url", label: "Google Play link" }, { name: "chef_cta_title", label: "Chef title" },
        { name: "chef_cta_button", label: "Chef button text" }, { name: "chef_cta_link", label: "Chef button link" },
        { name: "app_text", label: "App text", type: "textarea" }, { name: "chef_cta_text", label: "Chef text", type: "textarea" },
      ] },
    ],
  },
  banners: {
    title: "Website banners", description: "Image banners with a button, shown on the homepage under the promotions strip.",
    groups: [{ title: "Banners", fields: [{ name: "items", label: "Banners", type: "list", itemLabel: "Banner", fields: [
      { name: "title", label: "Title" }, { name: "subtitle", label: "Subtitle" },
      { name: "button_text", label: "Button text" }, { name: "button_link", label: "Button link" },
      { name: "active", label: "Show", type: "checkbox" }, { name: "image", label: "Image", type: "image" },
    ] }] }],
  },
  navigation: {
    title: "Navigation menu", description: "The links in your header menu, in order.",
    groups: [
      { title: "Header links", fields: [{ name: "header_links", label: "Menu links", type: "list", itemLabel: "Link", fields: LINK_FIELDS }] },
      { title: "Header options", fields: [
        { name: "show_search", label: "Show search", type: "checkbox" }, { name: "show_cart", label: "Show cart", type: "checkbox" },
        { name: "login_label", label: "Login button text" },
      ] },
    ],
  },
  footer: {
    title: "Footer", description: "Footer text, link columns and social media. Contact details come from General settings.",
    groups: [
      { title: "Text", fields: [{ name: "description", label: "Description", type: "textarea" }, { name: "copyright", label: "Copyright line" }] },
      { title: "Link columns", help: "Up to 4 columns are shown.", fields: [{ name: "columns", label: "Columns", type: "list", itemLabel: "Column", fields: [
        { name: "title", label: "Column title" }, { name: "links", label: "Links", type: "list", itemLabel: "Link", fields: LINK_FIELDS },
      ] }] },
      { title: "Social media", help: "Icons only show when a link is filled in.", fields: [
        { name: "instagram", label: "Instagram" }, { name: "facebook", label: "Facebook" }, { name: "twitter", label: "X / Twitter" }, { name: "youtube", label: "YouTube" },
      ] },
    ],
  },
  onboarding: {
    title: "Registration & onboarding", description: "Text and images on the sign-in and sign-up pages.",
    groups: [
      { title: "Sign-in page", fields: [
        { name: "login_title", label: "Title" }, { name: "login_subtitle", label: "Subtitle" },
        { name: "login_side_text", label: "Image caption" }, { name: "login_image", label: "Side image", type: "image" },
      ] },
      { title: "Sign-up page", fields: [
        { name: "register_title", label: "Title" }, { name: "register_subtitle", label: "Subtitle" },
        { name: "register_side_text", label: "Image caption" }, { name: "register_button", label: "Button text" },
        { name: "welcome_message", label: "Welcome message after sign-up" }, { name: "register_image", label: "Side image", type: "image" },
      ] },
    ],
  },
  pricing: {
    title: "Pricing & service info", description: "Service fee and info at checkout, plus the partner plans on the Business page.",
    groups: [
      { title: "Checkout", fields: [
        { name: "service_fee_percent", label: "Service fee (% of order)", type: "number" },
        { name: "service_info", label: "Service info shown at checkout", type: "textarea" },
      ] },
      { title: "Partner plans", fields: [{ name: "tiers", label: "Plans", type: "list", itemLabel: "Plan", fields: [
        { name: "name", label: "Name" }, { name: "price", label: "Price" }, { name: "sub", label: "Price note" },
        { name: "accent", label: "Highlight as most popular", type: "checkbox" },
        { name: "features", label: "Features (one per line)", type: "lines" },
      ] }] },
    ],
  },
  seo: {
    title: "SEO settings", description: "How your site appears in Google and when shared on social media.",
    groups: [
      { title: "Site-wide", fields: [
        { name: "meta_title", label: "Page title" }, { name: "keywords", label: "Keywords" },
        { name: "meta_description", label: "Description", type: "textarea" }, { name: "og_image", label: "Share image", type: "image" },
      ] },
      { title: "Per page", fields: [{ name: "pages", label: "Pages", type: "list", itemLabel: "Page", fields: [
        { name: "path", label: "Page", type: "select", options: PAGE_OPTIONS }, { name: "title", label: "Title" },
        { name: "description", label: "Description", type: "textarea" },
      ] }] },
    ],
  },
  visibility: {
    title: "Website visibility", description: "Turn sections and pages on or off, or close the whole site for maintenance. Admins can still see everything.",
    groups: [
      { title: "Maintenance", fields: [
        { name: "maintenance_mode", label: "Close site for maintenance", type: "checkbox" },
        { name: "maintenance_message", label: "Maintenance message", type: "textarea" },
      ] },
      { title: "Homepage sections", fields: [
        { name: "show_announcements", label: "Announcement bar", type: "checkbox" }, { name: "show_promos", label: "Promotions strip", type: "checkbox" },
        { name: "show_categories", label: "Food categories", type: "checkbox" }, { name: "show_featured_chefs", label: "Featured chefs", type: "checkbox" },
        { name: "show_featured_dishes", label: "Featured dishes", type: "checkbox" }, { name: "show_custom_sections", label: "Custom sections", type: "checkbox" },
        { name: "show_spotlight", label: "Dish spotlight", type: "checkbox" }, { name: "show_testimonials", label: "Testimonials", type: "checkbox" },
        { name: "show_app_cta", label: "App download", type: "checkbox" }, { name: "show_chef_cta", label: "Become a chef", type: "checkbox" },
      ] },
      { title: "Hidden pages", fields: [{ name: "hidden_pages", label: "Hide these pages from visitors", type: "multicheck", options: PAGE_OPTIONS.filter((p) => p.value !== "/") }] },
    ],
  },
};
